<!--
  StripeBindingsCard -- SuperAdmin "Stripe connections" card on the
  distributor edit page.

  Moves a distributor between its sandbox and live Stripe Connect accounts
  without re-running OAuth. PARK saves the current binding and clears it onto
  the other platform (so the distributor can connect the other account
  through Payments); SWITCH restores a saved binding. Park never restores
  anything, so its label and dialog say the other mode is left with no
  account, and point at Switch when a saved one is restorable: an operator
  read the old "Park ... and switch to live" as a way back to live and was
  left on live with no account (prod, distributor 5, audit 816). The server
  (`api/src/services/stripe_binding_park.py`) never revokes anything on
  Stripe's side -- it only changes which account the distributor row points
  at, and records the old binding in the audit log as the restore source.

  EVERY WRITE IS A DRY RUN FIRST. The dry run returns the exact changes; the
  admin reads them and must type the distributor's name before Apply enables.
  Retyping the name is the point: this card is one click away from a
  different tenant's card, and moving the wrong distributor onto the live
  platform moves real money.

  A 409 is a refusal with nothing written (stranded orders, no saved binding,
  account now held elsewhere, wrong platform). It is shown as-is, with the
  stranded-order count when the server sends one.
-->
<template>
    <section
        class="stripe-bindings"
        data-testid="stripe-bindings"
    >
        <h2 class="stripe-bindings__title">Stripe connections</h2>

        <p
            v-if="loading"
            data-testid="stripe-bindings-loading"
        >
            Loading Stripe connections...
        </p>

        <p
            v-else-if="loadError"
            class="stripe-bindings__error"
            data-testid="stripe-bindings-error"
        >
            {{ loadError }}
        </p>

        <template v-else-if="current">
            <div class="stripe-bindings__current">
                <!--
                  One static testid per mode, so a ui-runner scenario can wait
                  for the mode to CHANGE: its selectors are data-testid only,
                  and the testid linter cannot see template-string testids.
                -->
                <span
                    class="stripe-bindings__badge"
                    :class="`stripe-bindings__badge--${current.mode}`"
                    :data-mode="current.mode"
                    data-testid="stripe-bindings-mode-badge"
                ><span
                    v-if="current.mode === 'live'"
                    data-testid="stripe-bindings-current-live"
                >LIVE</span><span
                    v-else-if="current.mode === 'sandbox'"
                    data-testid="stripe-bindings-current-sandbox"
                >SANDBOX</span><span v-else>{{ modeLabel(current.mode) }}</span></span>
                <span data-testid="stripe-bindings-account">
                    Account: {{ current.account_masked || "none connected" }}
                </span>
                <span
                    v-if="current.account_masked"
                    data-testid="stripe-bindings-readiness"
                >
                    Readiness: {{ readinessText(current.readiness) }}
                    <em
                        v-if="current.readiness_source === 'saved'"
                        data-testid="stripe-bindings-readiness-saved"
                    >(from saved snapshot, not freshly verified)</em>
                </span>
            </div>

            <h3 class="stripe-bindings__subtitle">Saved connections</h3>
            <p
                v-if="!saved.length"
                class="stripe-bindings__hint"
                data-testid="stripe-bindings-saved-empty"
            >
                None saved yet. Parking a connection saves it here.
            </p>
            <table
                v-else
                class="stripe-bindings__saved"
                data-testid="stripe-bindings-saved"
            >
                <thead>
                    <tr>
                        <th>Mode</th>
                        <th>Account</th>
                        <th>Saved</th>
                        <th>By</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(row, i) in saved"
                        :key="`${row.mode}-${row.saved_at}-${i}`"
                        data-testid="stripe-bindings-saved-row"
                        :data-mode="row.mode"
                    >
                        <td>{{ modeLabel(row.mode) }}</td>
                        <td>{{ row.account_masked || "none" }}</td>
                        <td>{{ formatTime(row.saved_at) }}</td>
                        <td>{{ row.saved_by || "-" }}</td>
                    </tr>
                </tbody>
            </table>

            <p
                v-if="refusal"
                class="stripe-bindings__error"
                data-testid="stripe-bindings-refusal"
            >
                {{ refusal.message }}
                <span
                    v-if="refusal.stranded !== null"
                    data-testid="stripe-bindings-refusal-stranded"
                >
                    Stranded orders: {{ refusal.stranded }}
                </span>
            </p>

            <ul
                v-if="notices.length"
                class="stripe-bindings__warning"
                data-testid="stripe-bindings-notices"
            >
                <li
                    v-for="(notice, i) in notices"
                    :key="i"
                >
                    {{ notice }}
                </li>
            </ul>

            <!--
              Switch is the primary action whenever it can restore something;
              Park is secondary because it only ever leaves the other mode
              empty.
            -->
            <div class="stripe-bindings__actions">
                <v-btn
                    color="warning"
                    variant="outlined"
                    :disabled="busy"
                    data-testid="stripe-bindings-park"
                    @click="startPark"
                >Park {{ current.mode }} connection (leaves {{ otherMode }} with no account)</v-btn>
                <v-btn
                    color="primary"
                    :variant="canSwitchTo('live') ? 'flat' : 'outlined'"
                    :disabled="busy || !canSwitchTo('live')"
                    data-testid="stripe-bindings-switch-live"
                    @click="startSwitch('live')"
                >Switch to live</v-btn>
                <v-btn
                    color="primary"
                    :variant="canSwitchTo('sandbox') ? 'flat' : 'outlined'"
                    :disabled="busy || !canSwitchTo('sandbox')"
                    data-testid="stripe-bindings-switch-sandbox"
                    @click="startSwitch('sandbox')"
                >Switch to sandbox</v-btn>
            </div>
        </template>

        <v-dialog
            v-if="pending"
            :model-value="true"
            max-width="560"
            persistent
        >
            <v-card data-testid="stripe-bindings-confirm">
                <v-card-title data-testid="stripe-bindings-confirm-title">{{ pending.title }}</v-card-title>
                <v-card-text>
                    <p
                        v-if="pending.restorable"
                        class="stripe-bindings__warning"
                        data-testid="stripe-bindings-park-restorable"
                    >
                        A saved {{ pending.restorable }} connection exists. To go back
                        to it, use 'Switch to {{ pending.restorable }}' instead.
                    </p>
                    <p>This will change:</p>
                    <ul data-testid="stripe-bindings-confirm-changes">
                        <li
                            v-for="(change, i) in pending.changes"
                            :key="i"
                        >
                            {{ changeText(change) }}
                        </li>
                    </ul>
                    <ul
                        v-if="pending.warnings.length"
                        class="stripe-bindings__warning"
                        data-testid="stripe-bindings-confirm-warnings"
                    >
                        <li
                            v-for="(warning, i) in pending.warnings"
                            :key="i"
                        >
                            {{ warning }}
                        </li>
                    </ul>
                    <p>
                        Type <strong>{{ distributorName }}</strong> to confirm.
                    </p>
                    <v-text-field
                        v-model="confirmName"
                        label="Distributor name"
                        autocomplete="off"
                        data-testid="stripe-bindings-confirm-name"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        variant="text"
                        :disabled="applying"
                        data-testid="stripe-bindings-confirm-cancel"
                        @click="cancel"
                    >Cancel</v-btn>
                    <v-btn
                        color="error"
                        variant="flat"
                        :disabled="!nameMatches || applying"
                        :loading="applying"
                        data-testid="stripe-bindings-confirm-apply"
                        @click="apply"
                    >Apply</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </section>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import {
    getStripeBindings,
    parkStripeBinding,
    switchStripeBinding,
} from "@/services/superadminStripeModeService";

// Mirrors src/models/distributor.STRIPE_PLATFORM_MODES.
const MODES = ["live", "sandbox"];

export default {
    name: "StripeBindingsCard",

    props: {
        distributorId: { type: [String, Number], required: true },
    },

    emits: ["changed"],

    setup(props, { emit }) {
        const loading = ref(true);
        const loadError = ref("");
        const current = ref(null);
        const saved = ref([]);
        const canSwitch = ref([]);
        // From the server payload, so the name the admin must type is the
        // name of the row being moved, not whatever the page last showed.
        const distributorName = ref("");
        // Non-fatal server notes from the last applied move (for example,
        // readiness could not be refreshed from Stripe).
        const notices = ref([]);
        const busy = ref(false);
        const applying = ref(false);
        const refusal = ref(null);
        // { action: "park"|"switch", to, title, changes, warnings,
        //   restorable: the other mode when a park would skip past a saved
        //   binding that Switch could restore, else null }
        const pending = ref(null);
        const confirmName = ref("");

        const otherMode = computed(
            () => MODES.find((m) => m !== current.value?.mode) || ""
        );

        // Exact match, no trimming or case folding: the check is only worth
        // anything if it cannot be satisfied by accident.
        const nameMatches = computed(
            () =>
                distributorName.value !== "" &&
                confirmName.value === distributorName.value
        );

        function capitalize(text) {
            return text ? text[0].toUpperCase() + text.slice(1) : text;
        }

        function modeLabel(mode) {
            return (mode || "unknown").toUpperCase();
        }

        // The SERVER decides which modes a switch can restore
        // (`can_switch_to`): the newest saved binding per mode, excluding the
        // mode the distributor is already connected on. Re-deriving that here
        // from `saved` would be a second copy of the rule that can drift.
        function canSwitchTo(mode) {
            return canSwitch.value.includes(mode);
        }

        // `readiness` is the stored snapshot: { status, shopper_payments,
        // transfers, bank_payouts, verification, ... }. Shopper payments is
        // the one that decides whether this store can take a card.
        function readinessText(readiness) {
            if (!readiness || typeof readiness !== "object") return "unknown";
            const parts = [];
            if (readiness.status) parts.push(readiness.status);
            if (readiness.shopper_payments) {
                parts.push(`shopper payments ${readiness.shopper_payments}`);
            }
            return parts.length ? parts.join(", ") : "unknown";
        }

        // { field, from, to }, account ids already masked by the server.
        function changeText(change) {
            const show = (v) => (v === null || v === undefined || v === "" ? "(empty)" : v);
            return `${change.field}: ${show(change.from)} -> ${show(change.to)}`;
        }

        function formatTime(value) {
            if (!value) return "-";
            const d = new Date(value);
            return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
        }

        function apply409(err) {
            const data = err?.response?.data || {};
            const stranded = data.stranded_order_count;
            refusal.value = {
                message: data.message || data.error || "Refused. Nothing was changed.",
                stranded:
                    stranded === undefined || stranded === null ? null : stranded,
            };
        }

        function failure(err, fallback) {
            if (err?.response?.status === 409) {
                apply409(err);
                return;
            }
            refusal.value = {
                message: err?.response?.data?.message || fallback,
                stranded: null,
            };
        }

        async function load() {
            loadError.value = "";
            try {
                const { data } = await getStripeBindings(props.distributorId);
                current.value = data?.current || null;
                saved.value = Array.isArray(data?.saved) ? data.saved : [];
                canSwitch.value = Array.isArray(data?.can_switch_to)
                    ? data.can_switch_to
                    : [];
                distributorName.value = data?.distributor_name || "";
                if (!current.value) {
                    loadError.value = "Could not read the Stripe connections.";
                }
            } catch (err) {
                const status = err?.response?.status;
                loadError.value =
                    status === 403 || status === 404
                        ? "You do not have access to this distributor's Stripe connections."
                        : "Could not load the Stripe connections.";
            } finally {
                loading.value = false;
            }
        }

        function send(action, to, dryRun) {
            return action === "park"
                ? parkStripeBinding(props.distributorId, dryRun)
                : switchStripeBinding(props.distributorId, to, dryRun);
        }

        async function preview(action, to, title) {
            refusal.value = null;
            notices.value = [];
            busy.value = true;
            try {
                const { data } = await send(action, to, true);
                confirmName.value = "";
                const other = otherMode.value;
                pending.value = {
                    action,
                    to,
                    title,
                    restorable:
                        action === "park" && canSwitchTo(other) ? other : null,
                    changes: Array.isArray(data?.changes) ? data.changes : [],
                    warnings: Array.isArray(data?.warnings) ? data.warnings : [],
                };
            } catch (err) {
                failure(err, "Could not preview this change.");
            } finally {
                busy.value = false;
            }
        }

        function startPark() {
            return preview(
                "park",
                null,
                `Park the ${current.value.mode} connection? ${capitalize(otherMode.value)} will have no connected account until someone connects one.`
            );
        }

        function startSwitch(mode) {
            return preview("switch", mode, `Switch to the saved ${mode} connection?`);
        }

        function cancel() {
            pending.value = null;
            confirmName.value = "";
        }

        async function apply() {
            if (!pending.value || !nameMatches.value) return;
            const { action, to } = pending.value;
            applying.value = true;
            busy.value = true;
            try {
                const { data } = await send(action, to, false);
                pending.value = null;
                confirmName.value = "";
                notices.value = Array.isArray(data?.warnings) ? data.warnings : [];
                await load();
                emit("changed");
            } catch (err) {
                pending.value = null;
                failure(err, "The change failed. Nothing was saved.");
            } finally {
                applying.value = false;
                busy.value = false;
            }
        }

        onMounted(load);
        watch(
            () => props.distributorId,
            () => {
                loading.value = true;
                load();
            }
        );

        return {
            loading,
            loadError,
            current,
            saved,
            busy,
            applying,
            refusal,
            notices,
            distributorName,
            pending,
            confirmName,
            otherMode,
            nameMatches,
            modeLabel,
            canSwitchTo,
            readinessText,
            changeText,
            formatTime,
            startPark,
            startSwitch,
            cancel,
            apply,
        };
    },
};
</script>

<style scoped>
.stripe-bindings {
    margin-top: 24px;
}
.stripe-bindings__title {
    font-size: 1.1rem;
    margin-bottom: 8px;
}
.stripe-bindings__subtitle {
    font-size: 1rem;
    margin: 16px 0 4px;
}
.stripe-bindings__current {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
}
.stripe-bindings__badge {
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 12px;
    color: #fff;
}
.stripe-bindings__badge--live {
    background: #2e7d32;
}
.stripe-bindings__badge--sandbox {
    background: #b26a00;
}
.stripe-bindings__saved th,
.stripe-bindings__saved td {
    text-align: left;
    padding: 2px 12px 2px 0;
}
.stripe-bindings__hint {
    color: rgba(0, 0, 0, 0.6);
}
.stripe-bindings__error {
    color: #c62828;
    margin-top: 12px;
}
.stripe-bindings__warning {
    color: #8a5300;
    margin-top: 12px;
}
.stripe-bindings__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
}
</style>
